import { useSyncExternalStore } from "react";
import type { Hub } from "@foreman/app-core";
import { signInAvailable } from "./account";
import { switchGuestComputer } from "./identity";
import { setSignInTrigger, track } from "./analytics";
import { GoogleButton } from "./screens/SignIn";
import { Mascot, Sheet } from "./ui";

/**
 * The one place a guest is asked to sign in. Nothing on any screen announces a limit: the app looks fully open, and only when a
 * guest takes an action a guest can't (a second session, importing a chat, source control, earlier messages, a second computer)
 * does this sheet appear, at that last step. Signing in runs the action again by itself.
 */
export type Trigger = "second_session" | "import" | "git" | "history" | "second_computer";

interface Request {
  trigger: Trigger;
  /** Runs when the person closes the sheet without signing in ("Not now", or a tap outside it). */
  onDismiss?: () => void;
  /** Runs once the person is signed in (and, when `computerId` is given, that computer knows it). */
  resume?: () => void;
  computerId?: string;
  /** For a guest's second computer: the other way out, choosing this computer as the one to use (its public key, the relay's address for it). */
  switchTo?: { key: string; label: string };
}

let current: Request | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
const getCurrent = () => current;

/** Show the sign-in sheet for `trigger`. Does nothing (returns false) when sign-in isn't part of this build. */
export function askSignIn(req: Request): boolean {
  if (!signInAvailable()) return false;
  if (current?.trigger === req.trigger) return true;
  current = req;
  setSignInTrigger(req.trigger);
  track("signin_prompt_shown", { trigger: req.trigger });
  emit();
  return true;
}

function close(): void {
  current = null;
  setSignInTrigger(undefined);
  emit();
}

/** Which sheet an upgrade_required answer from the computer leads to. No feature means the running-session limit. */
export function triggerFor(feature?: string): Trigger {
  return feature === "sessions.import" ? "import" : feature === "git" ? "git" : feature === "history.conversation" ? "history" : "second_session";
}

/** Is this an answer that a sign-in fixes? */
export const needsSignIn = (e: unknown): e is Error & { code: string; feature?: string } => {
  const code = (e as { code?: string } | null)?.code;
  return code === "upgrade_required" || code === "sign_in_required"; // the second is what computers before this release say about the one-session limit
};

/**
 * For a catch block: if the failure is "this needs an account", open the sheet (run `retry` once signed in) and return true so the
 * caller shows nothing. Anything else returns false and is shown as before.
 */
export function signInIfNeeded(e: unknown, computerId: string, retry?: () => void): boolean {
  if (!needsSignIn(e)) return false;
  return askSignIn({ trigger: triggerFor(e.feature), computerId, resume: retry });
}

/** What each ask says: the same headline everywhere, and what signing in unlocks for this step. */
const WHY: Record<Trigger, string> = {
  second_session: "Guests can run one session at a time. Sign in to unlock unlimited sessions.",
  import: "Continuing a chat from your terminal needs an account. Sign in to unlock it, plus unlimited sessions and computers.",
  git: "Source control needs an account. Sign in to unlock it, plus unlimited sessions and computers.",
  history: "Earlier messages need an account. Sign in to unlock them, plus unlimited sessions and computers.",
  second_computer: "Guests can use one computer. Sign in to unlock unlimited computers, or switch to this one.",
};

export function SignInSheet({ hub }: { hub: Hub }) {
  const req = useSyncExternalStore(subscribe, getCurrent, getCurrent);
  if (!req) return null;
  const dismiss = () => {
    track("signin_prompt_dismissed", { trigger: req.trigger });
    const { onDismiss } = req;
    close();
    onDismiss?.();
  };
  const done = () => {
    const { resume, computerId, trigger } = req;
    close();
    if (!resume) return;
    // The computer learns of the sign-in a moment later; run the action again as soon as it does (or after a few seconds regardless).
    void (computerId ? hub.whenSignedIn(computerId) : Promise.resolve(true)).then(() => {
      track("action_resumed", { trigger });
      resume();
    });
  };
  const switchHere = async () => {
    const { switchTo, computerId } = req;
    if (!switchTo) return;
    const ok = await switchGuestComputer(switchTo.key);
    track(ok ? "guest_computer_switched" : "guest_computer_switch_failed", { trigger: req.trigger });
    if (ok) {
      close();
      if (computerId) hub.get(computerId)?.retryBlocked();
    }
  };
  return (
    <Sheet onClose={dismiss} top center>
      <div className="gate-modal" data-gate={req.trigger}>
        <Mascot size={84} />
        <div className="t">You’ve hit the guest limit</div>
        <p className="fine">{WHY[req.trigger]} Brocoded only learns your name and email, never your sessions, commands or files.</p>
        <div className="gate-actions">
          <GoogleButton onDone={done} />
          {req.switchTo && (
            <button className="btn secondary" onClick={() => void switchHere()}>
              Use {req.switchTo.label} instead
            </button>
          )}
          <button className="text-btn plain" onClick={dismiss}>
            Not now
          </button>
        </div>
      </div>
    </Sheet>
  );
}
